import type React from 'react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { OverlayContextType, OverlayElement, OverlayPerformance } from '../types/overlay';

const OverlayContext = createContext<OverlayContextType | undefined>(undefined);

export interface OverlayProviderProps {
  children: ReactNode;
}

/**
 * Enhanced OverlayProvider with performance monitoring and advanced features
 * Phase 4: Comprehensive TypeScript interfaces and performance optimizations
 */
export const OverlayProvider: React.FC<OverlayProviderProps> = ({ children }) => {
  const [overlays, setOverlays] = useState<OverlayElement[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Performance monitoring
  const performanceRef = useRef<OverlayPerformance>({
    renderTime: 0,
    componentCount: 0,
    lastUpdate: new Date(),
  });

  // Update performance metrics when overlays change
  useEffect(() => {
    const startTime = performance.now();

    performanceRef.current = {
      renderTime: performance.now() - startTime,
      componentCount: overlays.length,
      lastUpdate: new Date(),
    };
  }, [overlays]);

  // Core overlay management
  const addOverlay = useCallback((overlay: OverlayElement) => {
    try {
      setError(null);
      setOverlays((prev) => {
        // Prevent duplicate overlays
        const exists = prev.some((existing) => existing.id === overlay.id);
        if (exists) {
          console.warn(
            `Overlay with id "${overlay.id}" already exists. Updating instead of adding.`,
          );
          return prev.map((existing) =>
            existing.id === overlay.id ? { ...existing, ...overlay } : existing,
          );
        }

        // Sort by priority and zIndex
        const newOverlays = [...prev, overlay];
        return newOverlays.sort((a, b) => {
          // First sort by priority
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          const aPriority = priorityOrder[a.priority || 'medium'];
          const bPriority = priorityOrder[b.priority || 'medium'];

          if (aPriority !== bPriority) {
            return bPriority - aPriority;
          }

          // Then by zIndex
          return (b.zIndex || 10001) - (a.zIndex || 10001);
        });
      });

      // Call lifecycle hook
      overlay.lifecycle?.onMount?.();

      console.log(
        `Overlay "${overlay.id}" added successfully with priority: ${overlay.priority || 'medium'}`,
      );
    } catch (err) {
      const errorMessage = `Failed to add overlay "${overlay.id}": ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMessage);
      console.error(errorMessage);
      overlay.lifecycle?.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }, []);

  const removeOverlay = useCallback((id: string) => {
    try {
      setError(null);
      let removedOverlay: OverlayElement | undefined;

      setOverlays((prev) => {
        removedOverlay = prev.find((overlay) => overlay.id === id);
        if (!removedOverlay) {
          console.warn(`Overlay with id "${id}" not found for removal`);
          return prev;
        }
        return prev.filter((overlay) => overlay.id !== id);
      });

      // Call lifecycle hook
      removedOverlay?.lifecycle?.onUnmount?.();

      console.log(`Overlay "${id}" removed successfully`);
    } catch (err) {
      const errorMessage = `Failed to remove overlay "${id}": ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMessage);
      console.error(errorMessage);
    }
  }, []);

  const updateOverlay = useCallback((id: string, updates: Partial<OverlayElement>) => {
    try {
      setError(null);
      setOverlays((prev) => {
        const exists = prev.some((overlay) => overlay.id === id);
        if (!exists) {
          console.warn(`Overlay with id "${id}" not found for update`);
          return prev;
        }
        return prev.map((overlay) => (overlay.id === id ? { ...overlay, ...updates } : overlay));
      });
      console.log(`Overlay "${id}" updated successfully`);
    } catch (err) {
      const errorMessage = `Failed to update overlay "${id}": ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMessage);
      console.error(errorMessage);
    }
  }, []);

  const showOverlays = useCallback(() => {
    try {
      setError(null);
      setIsLoading(true);
      console.log('Showing overlays...');

      // Simulate any async operations if needed
      setTimeout(() => {
        setIsVisible(true);
        setIsLoading(false);

        // Call lifecycle hooks for visible overlays
        overlays.forEach((overlay) => {
          if (overlay.visible !== false) {
            overlay.lifecycle?.onShow?.();
          }
        });

        console.log(`${overlays.length} overlays are now visible`);
      }, 0);
    } catch (err) {
      const errorMessage = `Failed to show overlays: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMessage);
      setIsLoading(false);
      console.error(errorMessage);
    }
  }, [overlays]);

  const hideOverlays = useCallback(() => {
    try {
      setError(null);
      setIsVisible(false);

      // Call lifecycle hooks
      overlays.forEach((overlay) => {
        overlay.lifecycle?.onHide?.();
      });

      console.log('Overlays hidden');
    } catch (err) {
      const errorMessage = `Failed to hide overlays: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMessage);
      console.error(errorMessage);
    }
  }, [overlays]);

  const clearAllOverlays = useCallback(() => {
    try {
      setError(null);

      // Call lifecycle hooks before clearing
      overlays.forEach((overlay) => {
        overlay.lifecycle?.onUnmount?.();
      });

      setOverlays([]);
      setIsVisible(false);
      setIsLoading(false);
      console.log('All overlays cleared');
    } catch (err) {
      const errorMessage = `Failed to clear overlays: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMessage);
      console.error(errorMessage);
    }
  }, [overlays]);

  // Advanced management methods
  const getOverlay = useCallback(
    (id: string): OverlayElement | undefined => {
      return overlays.find((overlay) => overlay.id === id);
    },
    [overlays],
  );

  const getOverlaysByCategory = useCallback(
    (category: string): OverlayElement[] => {
      return overlays.filter((overlay) => overlay.category === category);
    },
    [overlays],
  );

  const toggleOverlay = useCallback(
    (id: string) => {
      const overlay = getOverlay(id);
      if (overlay) {
        updateOverlay(id, { visible: !overlay.visible });
      }
    },
    [getOverlay, updateOverlay],
  );

  const reorderOverlay = useCallback(
    (id: string, newZIndex: number) => {
      updateOverlay(id, { zIndex: newZIndex });
    },
    [updateOverlay],
  );

  // Performance utilities
  const getPerformanceMetrics = useCallback((): OverlayPerformance => {
    return { ...performanceRef.current };
  }, []);

  const optimizeRendering = useCallback(() => {
    console.log('Optimizing overlay rendering...');
    // Remove invisible overlays with low priority
    setOverlays((prev) =>
      prev.filter((overlay) => {
        if (overlay.visible === false && overlay.priority === 'low') {
          overlay.lifecycle?.onUnmount?.();
          return false;
        }
        return true;
      }),
    );
  }, []);

  // Memoized context value for performance
  const value: OverlayContextType = useMemo(
    () => ({
      overlays,
      isVisible,
      isLoading,
      error,
      performance: performanceRef.current,
      addOverlay,
      removeOverlay,
      updateOverlay,
      showOverlays,
      hideOverlays,
      clearAllOverlays,
      getOverlay,
      getOverlaysByCategory,
      toggleOverlay,
      reorderOverlay,
      getPerformanceMetrics,
      optimizeRendering,
    }),
    [
      overlays,
      isVisible,
      isLoading,
      error,
      addOverlay,
      removeOverlay,
      updateOverlay,
      showOverlays,
      hideOverlays,
      clearAllOverlays,
      getOverlay,
      getOverlaysByCategory,
      toggleOverlay,
      reorderOverlay,
      getPerformanceMetrics,
      optimizeRendering,
    ],
  );

  return <OverlayContext.Provider value={value}>{children}</OverlayContext.Provider>;
};

export const useOverlay = (): OverlayContextType => {
  const context = useContext(OverlayContext);
  if (context === undefined) {
    throw new Error('useOverlay must be used within an OverlayProvider');
  }
  return context;
};
